// routes/abtest.create._index/components/ChooseTestType/index.jsx

import React from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    DollarSign,
    Package,
    MousePointer,
    ShoppingCart,
    Truck,
    CreditCard
} from 'lucide-react';

const ChooseTestType = () => {
    const navigate = useNavigate();

    const testTypes = [
        {
            id: 'element',
            title: 'Element Test',
            icon: MousePointer,
            description: 'Test buttons, forms, colors, and UI elements',
            examples: ['Button colors', 'Form layouts', 'Call-to-action text', 'Element positioning'],
            available: true,
            color: 'green',
            popular: true
        },
        {
            id: 'pricing',
            title: 'Pricing Test',
            icon: DollarSign,
            description: 'Test different pricing strategies, discounts, and price displays',
            examples: ['Compare prices', 'Discount amounts', 'Currency display', 'Price formatting'],
            available: true,
            color: 'blue',
        },
        {
            id: 'cart',
            title: 'Cart & Discount',
            icon: ShoppingCart,
            description: 'Test cart experiences and discount strategies',
            examples: ['Cart notifications', 'Discount banners', 'Shipping thresholds', 'Urgency messages'],
            available: true,
            color: 'orange'
        },
        {
            id: 'product',
            title: 'Product Test',
            icon: Package,
            description: 'Test product recommendations, descriptions, and layouts',
            examples: ['Product descriptions', 'Image variations', 'Related products', 'Product badges'],
            available: true,
            color: 'purple'
        },
        {
            id: 'shipping',
            title: 'Shipping Test',
            icon: Truck,
            description: 'Test shipping options, messaging, and delivery information',
            examples: ['Shipping options', 'Delivery messages', 'Free shipping bars', 'Shipping costs'],
            available: true,
            color: 'cyan'
        },
        {
            id: 'checkout',
            title: 'Checkout Test',
            icon: CreditCard,
            description: 'Test checkout flow, forms, and payment options',
            examples: ['Checkout steps', 'Payment methods', 'Form fields', 'Trust signals'],
            available: false,
            color: 'red'
        }
    ];

    const getColorClasses = (color, available) => {
        const baseClasses = "bg-white rounded-xl p-5 transition-all duration-300 ease-in-out relative overflow-hidden";
        
        if (!available) {
            return `${baseClasses} cursor-default border-2 border-gray-200`;
        }

        const hoverClasses = {
            blue: 'hover:border-blue-500 hover:shadow-[0_12px_24px_rgba(59,130,246,0.15)] hover:-translate-y-1',
            purple: 'hover:border-purple-500 hover:shadow-[0_12px_24px_rgba(139,92,246,0.15)] hover:-translate-y-1',
            green: 'hover:border-green-500 hover:shadow-[0_12px_24px_rgba(16,185,129,0.15)] hover:-translate-y-1',
            orange: 'hover:border-amber-500 hover:shadow-[0_12px_24px_rgba(245,158,11,0.15)] hover:-translate-y-1',
            cyan: 'hover:border-cyan-500 hover:shadow-[0_12px_24px_rgba(6,182,212,0.15)] hover:-translate-y-1',
            red: 'hover:border-red-500 hover:shadow-[0_12px_24px_rgba(239,68,68,0.15)] hover:-translate-y-1'
        };

        return `${baseClasses} cursor-pointer border-2 border-gray-200 hover:shadow-[0_12px_24px_rgba(0,0,0,0.1)] ${hoverClasses[color] || ''}`;
    };

    const getIconClasses = (color) => {
        const iconColors = {
            blue: 'bg-blue-100 text-blue-500',
            purple: 'bg-purple-100 text-purple-500',
            green: 'bg-green-100 text-green-500',
            orange: 'bg-amber-100 text-amber-500',
            cyan: 'bg-cyan-100 text-cyan-500',
            red: 'bg-red-100 text-red-500'
        };

        return `w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 ${iconColors[color] || ''}`;
    };

    const handleTestTypeClick = (testType) => {
        if (testType.available) {
            navigate(`/abtest/create/config?type=${testType.id}`);
        }
    };

    return (
        <div className="max-w-[1400px] mx-auto px-6 py-5 min-h-screen bg-gray-50">
            {/* Header */}
            <div className="mb-5">
                <Link 
                    to="/abtest" 
                    className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 no-underline text-sm font-medium transition-all duration-200 hover:bg-gray-50 hover:border-gray-300"
                >
                    <ArrowLeft size={18} />
                    Back to Dashboard
                </Link>
            </div>

            {/* Title Section */}
            <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Choose Test Type
                </h1>
                <p className="text-base text-gray-600">
                    Select the type of A/B test you want to create for your store
                </p>
            </div>

            {/* Test Types Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-[1400px] mx-auto">
                {testTypes.map((testType) => {
                    const IconComponent = testType.icon;

                    return (
                        <div
                            key={testType.id}
                            className={getColorClasses(testType.color, testType.available)}
                            onClick={() => handleTestTypeClick(testType)}
                        >
                            {/* Popular Badge */}
                            {testType.popular && (
                                <div className="absolute top-3 right-3 bg-gradient-to-r from-green-500 to-green-600 text-white px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide">
                                    Most Popular
                                </div>
                            )}

                            {/* Coming Soon Badge */}
                            {!testType.available && (
                                <div className="absolute top-3 right-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white px-2.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide z-10">
                                    Coming Soon
                                </div>
                            )}

                            {/* Icon */}
                            <div className={getIconClasses(testType.color)}>
                                <IconComponent size={24} />
                            </div>

                            {/* Content */}
                            <div className="relative">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1.5">
                                    {testType.title}
                                </h3>
                                <p className="text-sm text-gray-600 mb-3 leading-snug">
                                    {testType.description}
                                </p>

                                {/* Examples */}
                                <div className="mt-3">
                                    <p className="text-xs font-semibold text-gray-900 mb-2">
                                        Examples:
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {testType.examples.map((example, index) => (
                                            <span 
                                                key={index} 
                                                className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded-md text-[11px] font-medium"
                                            >
                                                {example}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ChooseTestType;